/**
 * DecisionClassBadge Component
 * 
 * Displays the canonical Decision Class for a session.
 * Decision Class answers: what kind of judgment is being made?
 * This is distinct from Session Type, which answers: what is being prioritized?
 * 
 * Reference: 02 — Terminology and Glossary (v2.2)
 */

import { Tag } from 'lucide-react';
import { 
  CANONICAL_DECISION_CLASS_DESCRIPTIONS,
  CANONICAL_DECISION_CLASS_LABELS,
  CANONICAL_SESSION_TYPE_LABELS,
  SESSION_TYPE_TO_DECISION_CLASSES,
  SESSION_INTENT_TO_CANONICAL_SESSION_TYPE,
  SESSION_INTENT_TO_DECISION_CLASS_KIND,
  type CanonicalDecisionClassKind,
  type CanonicalSessionType,
  type DecisionClass,
  type SessionIntent,
  type SessionType,
} from '../../types/prioritization/session';

const DECISION_CLASS_COLORS: Record<CanonicalDecisionClassKind, { bg: string; text: string; border: string }> = {
  selection: { bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--slate)]' },
  range_selection: { bg: 'bg-[var(--green-dim)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]', border: 'border-[var(--green)]' },
  convergence: { bg: 'bg-[var(--mustard-dim)]', text: 'text-[var(--mustard)]', border: 'border-[var(--mustard)]' },
  force_ranking: { bg: 'bg-[var(--coral-dim)]', text: 'text-[var(--coral)]', border: 'border-[var(--coral)]' },
  review: { bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--slate)]' },
};

const SESSION_TYPE_COLORS: Record<CanonicalSessionType, { bg: string; text: string; border: string }> = {
  participants: { bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--slate)]' },
  item_pool: { bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--slate)]' },
  criteria: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  scales: { bg: 'bg-[var(--green-dim)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]', border: 'border-[var(--green)]' },
  values: { bg: 'bg-[var(--mustard-dim)]', text: 'text-[var(--mustard)]', border: 'border-[var(--mustard)]' },
  items: { bg: 'bg-[var(--hover-tint)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--rule)]' },
};

interface DecisionClassBadgeProps {
  /** Canonical intent - preferred source for deriving decision semantics */
  intent?: SessionIntent;
  /** Canonical decision class kind to display */
  decisionClassKind?: CanonicalDecisionClassKind;
  /** Legacy detailed decision class identifier */
  decisionClass?: DecisionClass;
  /** Legacy session type - mapped approximately for backward compatibility */
  sessionType?: SessionType;
  /** Show description tooltip */
  showTooltip?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Map older detailed decision identifiers into the canonical decision-class kind.
 */
function getDecisionClassKindFromLegacyDecisionClass(decisionClass: DecisionClass): CanonicalDecisionClassKind {
  switch (decisionClass) {
    case 'S4_VALUES_CHOICE_SELECTION':
      return 'range_selection';
    case 'S5_VALUE_SELECTION':
      return 'convergence';
    case 'S6_ITEM_PRIORITIZATION':
      return 'force_ranking';
    default:
      return 'selection';
  }
}

function getDecisionClassKindFromLegacySessionType(sessionType: SessionType): CanonicalDecisionClassKind {
  const classes = SESSION_TYPE_TO_DECISION_CLASSES[sessionType];
  return getDecisionClassKindFromLegacyDecisionClass(classes[classes.length - 1]);
}

function resolveDecisionClassKind({
  intent,
  decisionClassKind,
  decisionClass,
  sessionType,
}: Pick<DecisionClassBadgeProps, 'intent' | 'decisionClassKind' | 'decisionClass' | 'sessionType'>): CanonicalDecisionClassKind | undefined {
  if (decisionClassKind) return decisionClassKind;
  if (intent) return SESSION_INTENT_TO_DECISION_CLASS_KIND[intent];
  if (decisionClass) return getDecisionClassKindFromLegacyDecisionClass(decisionClass);
  if (sessionType) return getDecisionClassKindFromLegacySessionType(sessionType);
  return undefined;
}

export function DecisionClassBadge({
  intent,
  decisionClassKind,
  decisionClass,
  sessionType,
  showTooltip = true,
  size = 'md',
  className = '',
}: DecisionClassBadgeProps) {
  const effectiveClass = resolveDecisionClassKind({ intent, decisionClassKind, decisionClass, sessionType });
  
  if (!effectiveClass) {
    return null;
  }
  
  const label = CANONICAL_DECISION_CLASS_LABELS[effectiveClass];
  const description = CANONICAL_DECISION_CLASS_DESCRIPTIONS[effectiveClass];
  const colors = DECISION_CLASS_COLORS[effectiveClass];
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };
  
  return (
    <span
      className={`
        inline-flex items-center gap-1.5 font-medium rounded-full border
        ${colors.bg} ${colors.text} ${colors.border}
        ${sizeClasses[size]}
        ${className}
      `}
      title={showTooltip ? description : undefined}
    >
      <Tag className={size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'} />
      {label}
    </span>
  );
}

interface SessionTypeBadgeProps {
  intent?: SessionIntent;
  sessionType?: CanonicalSessionType;
  showTooltip?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function SessionTypeBadge({
  intent,
  sessionType,
  showTooltip = true,
  size = 'md',
  className = '',
}: SessionTypeBadgeProps) {
  const effectiveType = sessionType || (intent ? SESSION_INTENT_TO_CANONICAL_SESSION_TYPE[intent] : undefined);

  if (!effectiveType) {
    return null;
  }

  const label = CANONICAL_SESSION_TYPE_LABELS[effectiveType];
  const colors = SESSION_TYPE_COLORS[effectiveType];
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 font-medium rounded-full border
        ${colors.bg} ${colors.text} ${colors.border}
        ${sizeClasses[size]}
        ${className}
      `}
      title={showTooltip ? `Session type: ${label}` : undefined}
    >
      <Tag className={size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'} />
      {label}
    </span>
  );
}

/**
 * Compact version showing just the S-code
 */
export function DecisionClassCode({
  intent,
  decisionClassKind,
  decisionClass,
  sessionType,
  className = '',
}: Pick<DecisionClassBadgeProps, 'intent' | 'decisionClassKind' | 'decisionClass' | 'sessionType' | 'className'>) {
  const effectiveClass = resolveDecisionClassKind({ intent, decisionClassKind, decisionClass, sessionType });
  
  if (!effectiveClass) {
    return null;
  }
  
  const code = effectiveClass === 'force_ranking'
    ? 'FR'
    : effectiveClass === 'range_selection'
      ? 'RS'
      : effectiveClass === 'convergence'
        ? 'CV'
        : effectiveClass === 'review'
          ? 'RV'
          : 'SL';
  const colors = DECISION_CLASS_COLORS[effectiveClass];
  const description = CANONICAL_DECISION_CLASS_DESCRIPTIONS[effectiveClass];
  
  return (
    <span
      className={`
        inline-flex items-center justify-center w-8 h-8 text-xs font-bold rounded-full
        ${colors.bg} ${colors.text}
        ${className}
      `}
      title={description}
    >
      {code}
    </span>
  );
}

export default DecisionClassBadge;
